import React from 'react'
import styled, {css} from 'styled-components'

export interface TabItem {
  value: string
  label: string
  icon?: React.ReactNode
  count?: number
  color?: 'default' | 'navy' | 'orange'
  disabled?: boolean
}

export interface StyledTabsProps {
  tabs: TabItem[]
  activeTab: string
  onChange: (value: string) => void
  /** Visual style: underline (default) or pill (filled background on active) */
  variant?: 'underline' | 'pill'
  /** Allow horizontal scrolling when tabs overflow */
  scrollable?: boolean
  size?: 'sm' | 'md'
  className?: string
  'data-testid'?: string
}

const colorTokens = {
  default: {
    active: 'var(--brand-cta)',
    activeBg: 'var(--primary-50, #e4f6ff)',
    indicator: 'var(--brand-cta)',
  },
  navy: {
    active: 'var(--navy-900, var(--primary-800))',
    activeBg: 'var(--navy-50, #e8ecf1)',
    indicator: 'var(--navy-900, var(--primary-800))',
  },
  orange: {
    active: 'var(--feature-card-accent, #ff9500)',
    activeBg: 'var(--feature-card-bg, #fff8eb)',
    indicator: 'var(--feature-card-accent, #ff9500)',
  },
}

const TabsContainer = styled.div<{$variant: 'underline' | 'pill'; $scrollable: boolean}>`
  display: flex;
  position: relative;

  ${({$variant}) =>
    $variant === 'underline'
      ? css`
          gap: var(--space-2);
          border-bottom: var(--border-thin) solid var(--border-subtle);
        `
      : css`
          gap: 2px;
          background: var(--neutral-100);
          border-radius: var(--radius-sm);
          padding: 2px;
        `}

  ${({$scrollable}) =>
    $scrollable &&
    css`
      overflow-x: auto;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    `}
`

const TabButton = styled.button<{
  $isActive: boolean
  $color: 'default' | 'navy' | 'orange'
  $size: 'sm' | 'md'
  $variant: 'underline' | 'pill'
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  font-family: var(--font-sans);
  letter-spacing: var(--ls-normal);
  transition: color var(--motion-fast) var(--motion-ease),
    background var(--motion-fast) var(--motion-ease),
    border-color var(--motion-fast) var(--motion-ease);
  color: var(--text-secondary);

  ${({$size, $variant}) =>
    $variant === 'underline'
      ? css`
          /* Secondary / in-card — smaller subordinate strip */
          padding: var(--space-3) var(--space-1);
          font-size: var(--fs-caption);
          font-weight: var(--fw-regular);
        `
      : $size === 'sm'
      ? css`
          padding: var(--space-2) var(--space-3);
          font-size: var(--fs-caption);
          font-weight: var(--fw-medium);
        `
      : css`
          padding: var(--space-3) var(--space-5);
          font-size: var(--fs-body);
          font-weight: var(--fw-medium);
        `}

  /* Underline variant: bottom border indicator */
  ${({$variant}) =>
    $variant === 'underline' &&
    css`
      background: transparent;
      border-bottom: var(--border-thick) solid transparent;
      margin-bottom: calc(var(--border-thin) * -1);
    `}

  /* Pill variant: filled background on active */
  ${({$variant}) =>
    $variant === 'pill' &&
    css`
      background: transparent;
      border-radius: calc(var(--radius-sm) - 2px);
    `}

  ${({$isActive, $color, $variant}) =>
    $isActive &&
    ($variant === 'underline'
      ? css`
          color: ${colorTokens[$color].active};
          border-bottom-color: ${colorTokens[$color].indicator};
          font-weight: var(--fw-regular);
        `
      : css`
          background: var(--bg-card);
          color: ${colorTokens[$color].active};
          font-weight: var(--fw-semibold);
          box-shadow: var(--shadow-mid-strong);
        `)}

  &:hover:not(:disabled) {
    ${({$variant, $isActive}) =>
      $variant === 'pill' && !$isActive
        ? css`
            background: var(--bg-subtle);
          `
        : ''}
    color: ${({$color, $isActive, $variant}) =>
      $variant === 'underline' && !$isActive ? 'var(--text-primary)' : colorTokens[$color].active};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`

const CountPill = styled.span<{$isActive: boolean; $color: 'default' | 'navy' | 'orange'}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--radius-sm);
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
  background: ${({$isActive, $color}) =>
    $isActive ? colorTokens[$color].activeBg : 'var(--bg-2, var(--neutral-100))'};
  color: ${({$isActive, $color}) =>
    $isActive ? colorTokens[$color].active : 'var(--text-secondary)'};
`

export const Tabs: React.FC<StyledTabsProps> = ({
  tabs,
  activeTab,
  onChange,
  variant = 'underline',
  scrollable = false,
  size = 'md',
  className,
  'data-testid': testId,
}) => {
  return (
    <TabsContainer
      $variant={variant}
      $scrollable={scrollable}
      className={className}
      data-testid={testId}
      role="tablist">
      {tabs.map((tab) => {
        const isActive = tab.value === activeTab
        const color = tab.color ?? 'default'

        return (
          <TabButton
            key={tab.value}
            $isActive={isActive}
            $color={color}
            $size={size}
            $variant={variant}
            disabled={tab.disabled}
            onClick={() => onChange(tab.value)}
            data-testid={testId ? `${testId}-tab-${tab.value}` : undefined}
            role="tab"
            aria-selected={isActive}>
            {tab.icon}
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <CountPill
                $isActive={isActive}
                $color={color}
                data-testid={testId ? `${testId}-tab-${tab.value}-count` : undefined}>
                {tab.count}
              </CountPill>
            )}
          </TabButton>
        )
      })}
    </TabsContainer>
  )
}

export default Tabs
